import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useEffect } from 'react'

// ============================================================================
// Constants - Easy to update in one place
// ============================================================================

const TOKEN_INFO = {
  contractAddress: 'AKfgM9dUeynhvUy5e84LnFu6XxYW81xyu5tNBvS3pump',
  devWallet: 'Dm27n6vJsWSpdq93EMDdcx8rWkhcN2tvFcKeH67QBjjb',
  status: 'On bonding curve (pre-migration)',
  statusExplainer: 'The token is currently on the Pump.fun bonding curve. After migration to Raydium, liquidity will be auto-locked and ownership renounced by the Pump.fun flow.',
}

const TOKENOMICS = [
  { label: 'Total Supply', value: '1,000,000,000 $RSCAN' },
  { label: 'Chain', value: 'Solana' },
  { label: 'Buy/Sell Tax', value: '0%' },
  { label: 'Team Allocation', value: '0%' },
  { label: 'Mint Authority', value: 'Renounced after Pump.fun migration' },
  { label: 'Liquidity', value: 'Auto-locked on migration (LP burned by Pump.fun process)' },
]

const VERIFICATION_LINKS = [
  { label: 'View token on Solscan', href: 'https://solscan.io/token/AKfgM9dUeynhvUy5e84LnFu6XxYW81xyu5tNBvS3pump', comingSoon: false },
  { label: 'View Raydium pool', href: '#', comingSoon: true, comingSoonText: 'After migration' },
  { label: 'LP burn / migration proof', href: '#', comingSoon: true, comingSoonText: 'After migration' },
]

const ROADMAP = [
  {
    phase: 'Phase 1',
    title: 'Foundation',
    items: [
      'Launch $RSCAN on Pump.fun',
      'Publish token + dev wallet transparency page',
      'Basic status + verification info',
    ],
  },
  {
    phase: 'Phase 2',
    title: 'Core Tools',
    items: [
      'Dev wallet tracker dashboard',
      'Migration alerts (Pump.fun → Raydium)',
      'Sniper & bundler heatmap for new pairs',
    ],
  },
  {
    phase: 'Phase 3',
    title: 'Advanced Risk Tools',
    items: [
      'Contract risk scoring prototype',
      'Holder distribution visualizer',
      'Aggregate red-flag indicators',
    ],
  },
  {
    phase: 'Phase 4',
    title: 'Community & Governance',
    items: [
      'Community feedback loop on new features',
      'Potential rewards or perks for active $RSCAN holders',
    ],
  },
]

// ============================================================================
// Sub-components
// ============================================================================

interface InfoCardProps {
  label: string
  value: string
  helperText: string
  isCopyable?: boolean
}

function InfoCard({ label, value, helperText, isCopyable = false }: InfoCardProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="card p-6">
      <h3 className="text-sm font-semibold text-muted uppercase tracking-wider mb-3">
        {label}
      </h3>
      <div className="flex items-start gap-2 mb-3">
        <code className="font-mono text-sm text-white break-all bg-background/50 px-3 py-2 rounded-lg border border-surface-border flex-1">
          {value}
        </code>
        {isCopyable && (
          <button
            onClick={handleCopy}
            className="flex-shrink-0 p-2 rounded-lg bg-surface hover:bg-surface-hover border border-surface-border transition-colors"
            aria-label="Copy to clipboard"
          >
            {copied ? (
              <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        )}
      </div>
      <p className="text-sm text-muted leading-relaxed">{helperText}</p>
    </div>
  )
}

interface StatusCardProps {
  status: string
  explainer: string
}

function StatusCard({ status, explainer }: StatusCardProps) {
  return (
    <div className="card p-6">
      <h3 className="text-sm font-semibold text-muted uppercase tracking-wider mb-3">
        Status
      </h3>
      <div className="mb-3">
        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-warning/10 text-warning border border-warning/20">
          <span className="w-2 h-2 rounded-full bg-warning mr-2 animate-pulse" />
          {status}
        </span>
      </div>
      <p className="text-sm text-muted leading-relaxed">{explainer}</p>
    </div>
  )
}

interface RoadmapItemProps {
  phase: string
  title: string
  items: string[]
  isLast?: boolean
}

function RoadmapItem({ phase, title, items, isLast = false }: RoadmapItemProps) {
  return (
    <div className="relative pl-8 pb-8">
      {/* Timeline line */}
      {!isLast && (
        <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-surface-border" />
      )}
      {/* Timeline dot */}
      <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-primary" />
      </div>
      {/* Content */}
      <div>
        <span className="text-xs font-semibold text-primary uppercase tracking-wider">
          {phase}
        </span>
        <h4 className="text-lg font-semibold text-white mt-1 mb-3">{title}</h4>
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-muted">
              <svg className="w-4 h-4 text-primary/60 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ============================================================================
// Main TokenPage Component
// ============================================================================

export default function TokenPage() {
  // Update document title on mount
  useEffect(() => {
    document.title = 'RugScan Token ($RSCAN)'

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'RugScan Token ($RSCAN) is the community token funding development of RugScan.me — tools to track dev wallets, migrations, insiders, snipers, bundlers, and early red flags so traders don\'t get rugged.')
    }

    // Cleanup on unmount
    return () => {
      document.title = 'RugScan – Coming Soon'
    }
  }, [])

  return (
    <div className="flex flex-col items-center px-4 py-12 sm:py-16">
      <div className="w-full max-w-4xl">
        {/* ================================================================ */}
        {/* Hero Section */}
        {/* ================================================================ */}
        <section className="text-center mb-16">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <span className="text-sm font-medium text-primary-400">
              RugScan Token · $RSCAN
            </span>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            RugScan Token{' '}
            <span className="gradient-text">($RSCAN)</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg text-muted max-w-2xl mx-auto mb-6 leading-relaxed">
            $RSCAN is the community token that funds development of RugScan.me — tools for tracking dev wallets, migrations, insiders, snipers, bundlers, and early red flags so traders don't get rugged.
          </p>

          {/* Development note */}
          <div className="card inline-block p-4 max-w-xl">
            <p className="text-sm text-muted leading-relaxed">
              RugScan is still in active development. $RSCAN helps fund infrastructure, APIs, and feature development. This page is a transparency hub for the token.
            </p>
          </div>
        </section>

        {/* ================================================================ */}
        {/* Status + Key Info Cards */}
        {/* ================================================================ */}
        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <InfoCard
              label="Contract Address (Mint)"
              value={TOKEN_INFO.contractAddress}
              helperText="This is the official $RSCAN mint address on Solana."
              isCopyable
            />
            <InfoCard
              label="Dev Wallet (Transparent)"
              value={TOKEN_INFO.devWallet}
              helperText="Used only for hosting, APIs, servers, and development of RugScan tools. All activity is visible on Solana explorers."
              isCopyable
            />
            <StatusCard
              status={TOKEN_INFO.status}
              explainer={TOKEN_INFO.statusExplainer}
            />
          </div>
        </section>

        {/* ================================================================ */}
        {/* Tokenomics Section */}
        {/* ================================================================ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">Tokenomics</h2>

          <div className="card p-6 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TOKENOMICS.map((item, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 py-2 border-b border-surface-border/50 last:border-0">
                  <span className="text-sm text-muted font-medium min-w-[140px]">
                    {item.label}:
                  </span>
                  <span className="text-sm text-white">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-sm text-muted leading-relaxed">
            $RSCAN is intentionally simple. There are no hidden taxes, no team allocation carved out in the contract, and no ability to mint new tokens after migration. The goal is to align with RugScan's core value: transparency.
          </p>
        </section>

        {/* ================================================================ */}
        {/* Transparency & Verification Section */}
        {/* ================================================================ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">Transparency & Verification</h2>

          <p className="text-sm text-muted leading-relaxed mb-6">
            This page is meant to make $RSCAN as transparent as possible. As the token migrates and stabilizes, this section will be updated with direct links to on-chain proof.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {VERIFICATION_LINKS.map((link, idx) => (
              <a
                key={idx}
                href={link.href}
                target={link.comingSoon ? undefined : '_blank'}
                rel={link.comingSoon ? undefined : 'noopener noreferrer'}
                className="card p-4 hover:bg-surface-hover transition-colors group"
                onClick={(e) => link.comingSoon && e.preventDefault()}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-white block">{link.label}</span>
                    {link.comingSoon && (
                      <span className="text-xs text-muted">{link.comingSoonText || 'Coming soon'}</span>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* ================================================================ */}
        {/* Roadmap Section */}
        {/* ================================================================ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">Roadmap</h2>
          <p className="text-sm text-muted mb-8">
            Features funded by $RSCAN and planned for RugScan.me:
          </p>

          <div className="card p-6 sm:p-8">
            {ROADMAP.map((item, idx) => (
              <RoadmapItem
                key={idx}
                phase={item.phase}
                title={item.title}
                items={item.items}
                isLast={idx === ROADMAP.length - 1}
              />
            ))}
          </div>
        </section>

        {/* ================================================================ */}
        {/* CTA Section */}
        {/* ================================================================ */}
        <section className="text-center">
          <p className="text-sm text-muted mb-6">
            RugScan is being built in public. Follow along as we ship features funded by $RSCAN.
          </p>
          <Link
            to="/"
            className="btn-primary inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to RugScan.me
          </Link>
        </section>
      </div>
    </div>
  )
}
